import { useState } from "react";
import { Heart, MessageCircle, MapPin, MoreVertical, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Avatar } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

const statusClass = {
  open: "te-badge te-badge-open",
  in_progress: "te-badge te-badge-progress",
  resolved: "te-badge te-badge-resolved",
};
const statusLabel = { open: "Open", in_progress: "In Progress", resolved: "Resolved" };

export function ReportCard({ report, onDelete, onUpdate }) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(report.liked_by_me);
  const [likes, setLikes] = useState(report.likes_count);
  const [busy, setBusy] = useState(false);

  const toggleLike = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${report.id}/like`);
      setLiked(data.liked);
      setLikes(data.likes_count);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this report?")) return;
    try {
      await api.delete(`/reports/${report.id}`);
      toast.success("Report deleted");
      onDelete?.(report.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canDelete = user && (user.id === report.author?.id || user.role === "admin");

  return (
    <article data-testid={`report-card-${report.id}`} className="te-card te-card-hover p-5 md:p-6 te-fade-up">
      <header className="flex items-start gap-3">
        <Avatar user={report.author} size={40} />
        <div className="flex-1 min-w-0">
          <Link to={`/profile/${report.author?.id}`} className="font-medium text-[var(--te-text)] hover:underline" data-testid={`report-author-${report.id}`}>
            {report.author?.name || "Unknown"}
          </Link>
          <div className="flex items-center gap-2 text-xs text-[var(--te-text-muted)]">
            <span>{timeAgo(report.created_at)}</span>
            <span>·</span>
            <span className="capitalize">{report.category?.replace("_", " ")}</span>
            {report.address && (
              <>
                <span>·</span>
                <span className="flex items-center gap-1"><MapPin size={11} />{report.address}</span>
              </>
            )}
          </div>
        </div>
        <span className={statusClass[report.status] || "te-badge te-badge-open"}>
          {statusLabel[report.status] || "Open"}
        </span>
        {canDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`report-menu-${report.id}`} className="p-1.5 rounded-full hover:bg-[var(--te-input-bg)]">
                <MoreVertical size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDelete} className="text-[#DC2626]" data-testid={`report-delete-${report.id}`}>
                <Trash2 size={14} className="mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>

      <Link to={`/reports/${report.id}`} data-testid={`report-link-${report.id}`}>
        <h3 className="font-heading text-lg sm:text-xl font-semibold mt-3 mb-1.5 text-[var(--te-text)]">{report.title}</h3>
        <p className="text-sm sm:text-base text-[var(--te-text-secondary)] leading-relaxed line-clamp-3">{report.description}</p>
        {report.image_url && (
          <img src={report.image_url} alt={report.title} className="mt-4 rounded-xl w-full max-h-[420px] object-cover border border-[var(--te-divider)]" />
        )}
      </Link>

      <footer className="flex items-center gap-2 mt-4 pt-4 border-t border-[var(--te-input-bg)]">
        <button
          onClick={toggleLike}
          disabled={busy}
          data-testid={`report-like-${report.id}`}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            liked ? "bg-[var(--te-danger-soft)] text-[#DC2626]" : "text-[var(--te-text-secondary)] hover:bg-[var(--te-input-bg)]"
          }`}
        >
          <Heart size={16} fill={liked ? "currentColor" : "none"} /> {likes}
        </button>
        <Link
          to={`/reports/${report.id}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-[var(--te-text-secondary)] hover:bg-[var(--te-input-bg)]"
          data-testid={`report-comments-${report.id}`}
        >
          <MessageCircle size={16} /> {report.comments_count || 0}
        </Link>
      </footer>
    </article>
  );
}

export function PostCard({ post, onDelete }) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likes, setLikes] = useState(post.likes_count);
  const [busy, setBusy] = useState(false);

  const toggleLike = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(data.liked);
      setLikes(data.likes_count);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Post deleted");
      onDelete?.(post.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canDelete = user && (user.id === post.author?.id || user.role === "admin");

  return (
    <article data-testid={`post-card-${post.id}`} className="te-card te-card-hover p-5 md:p-6 te-fade-up">
      <header className="flex items-start gap-3">
        <Avatar user={post.author} size={40} />
        <div className="flex-1 min-w-0">
          <Link to={`/profile/${post.author?.id}`} className="font-medium text-[var(--te-text)] hover:underline">
            {post.author?.name || "Unknown"}
          </Link>
          <div className="text-xs text-[var(--te-text-muted)]">{timeAgo(post.created_at)}</div>
        </div>
        {canDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`post-menu-${post.id}`} className="p-1.5 rounded-full hover:bg-[var(--te-input-bg)]">
                <MoreVertical size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDelete} className="text-[#DC2626]" data-testid={`post-delete-${post.id}`}>
                <Trash2 size={14} className="mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>

      <p className="mt-3 text-[var(--te-text)] leading-relaxed whitespace-pre-wrap">{post.content}</p>
      {post.image_url && (
        <img src={post.image_url} alt="" className="mt-4 rounded-xl w-full max-h-[420px] object-cover border border-[var(--te-divider)]" />
      )}

      <footer className="flex items-center gap-2 mt-4 pt-4 border-t border-[var(--te-input-bg)]">
        <button
          onClick={toggleLike}
          disabled={busy}
          data-testid={`post-like-${post.id}`}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            liked ? "bg-[var(--te-danger-soft)] text-[#DC2626]" : "text-[var(--te-text-secondary)] hover:bg-[var(--te-input-bg)]"
          }`}
        >
          <Heart size={16} fill={liked ? "currentColor" : "none"} /> {likes}
        </button>
        <Link
          to={`/posts/${post.id}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-[var(--te-text-secondary)] hover:bg-[var(--te-input-bg)]"
          data-testid={`post-comments-${post.id}`}
        >
          <MessageCircle size={16} /> {post.comments_count || 0}
        </Link>
      </footer>
    </article>
  );
}

export { timeAgo };
